import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Check, ClipboardCheck, ArrowRight, ArrowLeft, FileDown, Loader2, CheckCircle, Eye, Plus, Trash2, Save, Upload } from "lucide-react";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import AppHeader from "@/components/AppHeader";
import { saveDraft, loadDraft, deleteDraft } from "@/utils/offlineStorage";
import { queueForSync } from "@/utils/syncQueue";
import BuyCreditsModal from "@/components/BuyCreditsModal";
import { useCreditPrompt } from "@/hooks/useCreditPrompt";
import { authFetch } from "@/utils/authFetch";
import PdfPreviewModal from "@/components/PdfPreviewModal";
import SignaturePad from "@/components/SignaturePad";
import TemplateBar, { SaveTemplateButton } from "@/components/TemplateBar";

const API = process.env.REACT_APP_BACKEND_URL;

const Label = ({ children }) => (
  <label className="block text-xs uppercase tracking-[0.15em] font-bold mb-1.5" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-secondary)' }}>{children}</label>
);

const PFS_OPTIONS = [
  { value: "P", label: "Pass" },
  { value: "F", label: "Fail" },
  { value: "S", label: "Skip / N/A" },
];

const EMPTY_APPLIANCE = {
  location: "", appliance_id: "", description: "", test_date: "",
  engineer: "", visual: "P", polarity: "P",
  earth_ohms: "", earth_result: "P",
  insulation_mohms: "", insulation_result: "P",
  overall: "PASS", fail_reason: "", notes: "",
};

const STEPS = [
  { num: 1, label: "Report Details" },
  { num: 2, label: "Appliances" },
  { num: 3, label: "Review & Generate" },
];

export default function PATTestingWizard() {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [draftSaved, setDraftSaved] = useState(false);
  const [signature, setSignature] = useState("");
  const { showModal, modalCreditType, promptBuyCredits, closeModal, buyCredits } = useCreditPrompt();
  const draftKey = "draft_pat_testing";

  const [report, setReport] = useState({
    client_company: "", client_address: "", client_contact: "", client_email: "",
    order_no: "", cert_number: "", test_period: null,
    tester_serial: "", tester_make_model: "", tester_cert_no: "", tester_cal_due: "",
    contractor_name: user?.company || "", contractor_email: user?.email || "",
    contractor_phone: "", engineer_name: user?.name || "",
  });

  const [appliances, setAppliances] = useState([{ ...EMPTY_APPLIANCE }]);

  // Auto-save every 10s
  useEffect(() => {
    if (completed) return;
    const timer = setInterval(() => {
      saveDraft(draftKey, { report, appliances }).then(() => setDraftSaved(true));
    }, 10000);
    return () => clearInterval(timer);
  }, [report, appliances, completed]);

  const manualSaveDraft = async () => {
    await saveDraft(draftKey, { report, appliances });
    setDraftSaved(true);
    toast.success("Draft saved locally");
  };

  const [editCertId, setEditCertId] = useState(null);

  // Load edit data or draft on mount
  useEffect(() => {
    const editData = sessionStorage.getItem("edit_cert_data");
    if (editData) {
      try {
        const cert = JSON.parse(editData);
        sessionStorage.removeItem("edit_cert_data");
        setEditCertId(cert._edit_id);
        setReport((p) => ({ ...p, ...cert }));
        if (cert.appliances?.length) setAppliances(cert.appliances);
        toast.info("Editing report — update and regenerate for free");
        return;
      } catch { /* ignore */ }
    }
    const repeatData = sessionStorage.getItem("repeat_cert_data");
    if (repeatData) {
      try {
        const prefill = JSON.parse(repeatData);
        sessionStorage.removeItem("repeat_cert_data");
        setReport((p) => ({ ...p, ...prefill }));
        toast.success("Customer and address details pre-filled from previous job");
        return;
      } catch { /* ignore */ }
    }
    loadDraft(draftKey).then((draft) => {
      if (draft) {
        if (draft.report) setReport((p) => ({ ...p, ...draft.report }));
        if (draft.appliances?.length) setAppliances(draft.appliances);
        toast.info("Restored from saved draft");
      }
    }).catch(() => {});
  }, []);

  const setR = (f) => (e) => setReport((p) => ({ ...p, [f]: e.target.value }));

  const addAppliance = () => setAppliances((prev) => [...prev, { ...EMPTY_APPLIANCE }]);
  const removeAppliance = (idx) => {
    if (appliances.length > 1) setAppliances((prev) => prev.filter((_, i) => i !== idx));
  };
  const updateAppliance = (idx, field, val) => {
    setAppliances((prev) => prev.map((a, i) => {
      if (i !== idx) return a;
      const updated = { ...a, [field]: val };
      // Auto-calculate overall status
      if (["visual", "polarity", "earth_result", "insulation_result"].includes(field)) {
        const results = [updated.visual, updated.polarity, updated.earth_result, updated.insulation_result];
        updated.overall = results.some((r) => r === "F") ? "FAIL" : "PASS";
      }
      return updated;
    }));
  };

  const duplicateAppliance = (idx) => {
    const clone = { ...appliances[idx], appliance_id: "" };
    setAppliances((prev) => [...prev.slice(0, idx + 1), clone, ...prev.slice(idx + 1)]);
  };

  const [csvOpen, setCsvOpen] = useState(false);
  const [csvText, setCsvText] = useState("");
  const fileRef = useRef(null);

  const parseCSV = (text) => {
    const lines = text.trim().split("\n").filter(Boolean);
    if (lines.length < 2) { toast.error("CSV needs a header row + at least 1 data row"); return; }
    // Parse header
    const header = lines[0].split(",").map((h) => h.trim().toLowerCase().replace(/[^a-z0-9]/g, "_"));
    const colMap = {};
    header.forEach((h, i) => {
      if (h.includes("location") || h.includes("loc")) colMap.location = i;
      else if (h.includes("appliance") && h.includes("id") || h === "id" || h === "asset_id") colMap.appliance_id = i;
      else if (h.includes("desc") || h.includes("appliance") && !h.includes("id")) colMap.description = i;
      else if (h.includes("visual")) colMap.visual = i;
      else if (h.includes("polarity") || h.includes("pol")) colMap.polarity = i;
      else if (h.includes("earth") && (h.includes("ohm") || h.includes("cont"))) colMap.earth_ohms = i;
      else if (h.includes("insul") && (h.includes("mohm") || h.includes("meg"))) colMap.insulation_mohms = i;
      else if (h.includes("fail") && h.includes("reason")) colMap.fail_reason = i;
      else if (h.includes("note")) colMap.notes = i;
      // fallback: first unknown text col -> description, second -> location
      else if (!colMap.description) colMap.description = i;
      else if (!colMap.location) colMap.location = i;
    });

    const imported = [];
    for (let r = 1; r < lines.length; r++) {
      const cells = lines[r].split(",").map((c) => c.trim().replace(/^"|"$/g, ""));
      if (cells.every((c) => !c)) continue;
      const row = { ...EMPTY_APPLIANCE };
      if (colMap.location !== undefined) row.location = cells[colMap.location] || "";
      if (colMap.appliance_id !== undefined) row.appliance_id = cells[colMap.appliance_id] || "";
      if (colMap.description !== undefined) row.description = cells[colMap.description] || "";
      if (colMap.visual !== undefined) row.visual = mapPFS(cells[colMap.visual]);
      if (colMap.polarity !== undefined) row.polarity = mapPFS(cells[colMap.polarity]);
      if (colMap.earth_ohms !== undefined) row.earth_ohms = cells[colMap.earth_ohms] || "";
      if (colMap.insulation_mohms !== undefined) row.insulation_mohms = cells[colMap.insulation_mohms] || "";
      if (colMap.fail_reason !== undefined) row.fail_reason = cells[colMap.fail_reason] || "";
      if (colMap.notes !== undefined) row.notes = cells[colMap.notes] || "";
      // auto-calculate overall
      const results = [row.visual, row.polarity, row.earth_result, row.insulation_result];
      row.overall = results.some((v) => v === "F") ? "FAIL" : "PASS";
      imported.push(row);
    }
    if (!imported.length) { toast.error("No valid rows found in CSV"); return; }
    // Replace the single empty default row, or append
    if (appliances.length === 1 && !appliances[0].appliance_id && !appliances[0].description) {
      setAppliances(imported);
    } else {
      setAppliances((prev) => [...prev, ...imported]);
    }
    toast.success(`Imported ${imported.length} appliances from CSV`);
    setCsvText("");
    setCsvOpen(false);
  };

  const mapPFS = (val) => {
    if (!val) return "P";
    const v = val.toUpperCase().trim();
    if (v === "F" || v === "FAIL") return "F";
    if (v === "S" || v === "SKIP" || v === "N/A" || v === "NA") return "S";
    return "P";
  };

  const handleCSVFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => { setCsvText(ev.target.result); };
    reader.readAsText(file);
    e.target.value = "";
  };

  const passCount = appliances.filter((a) => a.overall === "PASS").length;
  const failCount = appliances.filter((a) => a.overall === "FAIL").length;

  const buildPayload = () => ({
    ...report,
    test_period: report.test_period ? format(report.test_period, "dd/MM/yyyy") : "",
    signature,
    appliances,
  });

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const res = await authFetch(`${API}/api/certificates/pat/preview`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify(buildPayload()),
      });
      if (!res.ok) throw new Error("Preview failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      setShowPreview(true);
    } catch (err) { toast.error(err.message); } finally { setPreviewing(false); }
  };

  const handleGenerate = async () => {
    if (!navigator.onLine) {
      await queueForSync({ endpoint: "/api/certificates/pat", method: "POST", payload: buildPayload(), formTitle: "PAT Testing Report" });
      await manualSaveDraft();
      toast.success("You're offline — PAT report queued and will auto-submit when back online.");
      return;
    }
    setSubmitting(true);
    try {
      const isEdit = !!editCertId;
      const url = isEdit ? `${API}/api/certificates/${editCertId}` : `${API}/api/certificates/pat`;
      const method = isEdit ? "PUT" : "POST";
      const res = await authFetch(url, {
        method, headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify(buildPayload()),
      });
      if (res.status === 402) {
        promptBuyCredits("pat");
        return;
      }
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Failed");
      const data = await res.json();
      const certId = data.certificate_id || editCertId;
      setPdfUrl(`${API}/api/certificates/domestic-eic/${certId}/download`);
      setCompleted(true);
      toast.success(isEdit ? "PAT Report updated!" : "PAT Report generated! 1 credit used.");
      deleteDraft(draftKey).catch(() => {});
      authFetch(`${API}/api/user/contractor-details`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ contractor_trading_title: report.contractor_name, contractor_email: report.contractor_email, contractor_phone: report.contractor_phone }),
      }).catch(() => {});
    } catch (err) {
      if (!navigator.onLine || err.message === "Failed to fetch") {
        await queueForSync({ endpoint: "/api/certificates/pat", method: "POST", payload: buildPayload(), formTitle: "PAT Testing Report" });
        toast.success("Network lost — PAT report queued for auto-sync.");
      } else {
        toast.error(err.message);
      }
    } finally { setSubmitting(false); }
  };

  if (completed) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-page)' }}><AppHeader />
        <div className="max-w-3xl mx-auto px-4 py-12">
          <div className="form-container text-center" data-testid="pat-complete">
            <CheckCircle className="w-16 h-16 text-[#8B5CF6] mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>PAT Report Generated</h2>
            <p className="text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>{appliances.length} appliances tested — {passCount} passed, {failCount} failed</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
              {pdfUrl && <a href={pdfUrl} target="_blank" rel="noopener noreferrer" data-testid="pat-download-pdf" className="brutalist-btn flex items-center justify-center gap-2"><FileDown className="w-4 h-4" /> Download PDF</a>}
              <a href="/dashboard" className="brutalist-btn-outline flex items-center justify-center gap-2">Back to Dashboard</a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-page)' }} data-testid="pat-testing-wizard">
      <AppHeader />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center gap-3 mb-6">
          <ClipboardCheck className="w-6 h-6 text-[#8B5CF6]" />
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>PAT Testing Report</h1>
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-between mb-8" data-testid="pat-step-indicator">
          {STEPS.map((s, i) => (
            <div key={s.num} className="flex items-center flex-1">
              <div className="flex flex-col items-center">
                <div className={`w-10 h-10 flex items-center justify-center border-2 text-sm font-bold transition-all ${step > s.num ? "bg-[#8B5CF6] text-white border-[#8B5CF6]" : step === s.num ? "bg-[#8B5CF6] text-white border-[#8B5CF6]" : "bg-white text-[#B8B9BD] border-[#E4E4E7]"}`}>
                  {step > s.num ? <Check className="w-5 h-5" /> : s.num}
                </div>
                <span className={`text-xs mt-2 font-medium text-center hidden sm:block ${step >= s.num ? "" : "text-[#B8B9BD]"}`} style={{ fontFamily: 'Outfit, sans-serif', color: step >= s.num ? 'var(--text-primary)' : undefined }}>{s.label}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-2 sm:mx-4 ${step > s.num ? "bg-[#8B5CF6]" : "bg-[#E4E4E7]"}`} />}
            </div>
          ))}
        </div>

        {/* STEP 1: Report Details */}
        {step === 1 && (
          <div className="form-container max-w-3xl mx-auto" data-testid="pat-step-1">
            <TemplateBar formType="pat_testing" onLoad={(data) => {
              if (data.report) setReport(r => ({ ...r, ...data.report }));
            }} />
            <div className="border-b-2 pb-5 mb-5" style={{ borderColor: 'var(--border-light)' }}>
              <h3 className="text-sm font-bold text-[#8B5CF6] uppercase tracking-[0.15em] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Client Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2"><Label>Company name</Label><input className="brutalist-input" value={report.client_company} onChange={setR("client_company")} data-testid="pat-client-company" /></div>
                <div className="md:col-span-2"><Label>Address</Label><input className="brutalist-input" value={report.client_address} onChange={setR("client_address")} data-testid="pat-client-address" /></div>
                <div><Label>Contact name</Label><input className="brutalist-input" value={report.client_contact} onChange={setR("client_contact")} /></div>
                <div><Label>Email</Label><input className="brutalist-input" type="email" value={report.client_email} onChange={setR("client_email")} /></div>
              </div>
            </div>

            <div className="border-b-2 pb-5 mb-5" style={{ borderColor: 'var(--border-light)' }}>
              <h3 className="text-sm font-bold text-[#8B5CF6] uppercase tracking-[0.15em] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Report Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><Label>Order / Reference No</Label><input className="brutalist-input" value={report.order_no} onChange={setR("order_no")} /></div>
                <div><Label>Certificate number</Label><input className="brutalist-input" value={report.cert_number} onChange={setR("cert_number")} /></div>
                <div>
                  <Label>Test Period Commencing</Label>
                  <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                    <PopoverTrigger asChild>
                      <button className="brutalist-input flex items-center justify-between cursor-pointer w-full" type="button">
                        <span className={report.test_period ? "" : "text-[#9CA3AF]"}>{report.test_period ? format(report.test_period, "dd/MM/yyyy") : "Select date"}</span>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 border-2 border-[#0A0A0A] rounded-none" align="start">
                      <Calendar mode="single" selected={report.test_period} onSelect={(d) => { setReport((p) => ({ ...p, test_period: d })); setCalendarOpen(false); }} />
                    </PopoverContent>
                  </Popover>
                </div>
                <div><Label>Engineer name</Label><input className="brutalist-input" value={report.engineer_name} onChange={setR("engineer_name")} /></div>
              </div>
            </div>

            <div className="border-b-2 pb-5 mb-5" style={{ borderColor: 'var(--border-light)' }}>
              <h3 className="text-sm font-bold text-[#8B5CF6] uppercase tracking-[0.15em] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>PAT Tester Equipment</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><Label>Serial number</Label><input className="brutalist-input" value={report.tester_serial} onChange={setR("tester_serial")} /></div>
                <div><Label>Make & model</Label><input className="brutalist-input" value={report.tester_make_model} onChange={setR("tester_make_model")} /></div>
                <div><Label>Calibration cert no</Label><input className="brutalist-input" value={report.tester_cert_no} onChange={setR("tester_cert_no")} /></div>
                <div><Label>Calibration due date</Label><input className="brutalist-input" value={report.tester_cal_due} onChange={setR("tester_cal_due")} placeholder="e.g. 15/03/2027" /></div>
              </div>
            </div>

            <div className="pb-2 mb-2">
              <h3 className="text-sm font-bold text-[#8B5CF6] uppercase tracking-[0.15em] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Contractor Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><Label>Company name</Label><input className="brutalist-input" value={report.contractor_name} onChange={setR("contractor_name")} /></div>
                <div><Label>Email</Label><input className="brutalist-input" value={report.contractor_email} onChange={setR("contractor_email")} /></div>
                <div><Label>Phone</Label><input className="brutalist-input" value={report.contractor_phone} onChange={setR("contractor_phone")} /></div>
              </div>
            </div>

            <div className="flex justify-between items-center mt-4">
              <button type="button" onClick={manualSaveDraft} className="brutalist-btn-outline !px-3 !py-1.5 text-xs flex items-center gap-1.5" data-testid="pat-save-draft-btn">
                <Save className="w-3.5 h-3.5" /> {draftSaved ? "Draft Saved" : "Save Draft"}
              </button>
              <button data-testid="pat-step-1-next" onClick={() => setStep(2)} className="brutalist-btn flex items-center gap-2">
                Appliances <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* STEP 2: Appliance Testing */}
        {step === 2 && (
          <div className="form-container max-w-full mx-auto" data-testid="pat-step-2">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Appliance Details & Test Results</h2>
                <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>P = Pass / F = Fail / S = Skip (or test not applicable)</p>
              </div>
              <div className="flex items-center gap-3 text-sm font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <span className="text-green-600">{passCount} Pass</span>
                <span className="text-red-600">{failCount} Fail</span>
                <span style={{ color: 'var(--text-muted)' }}>{appliances.length} Total</span>
              </div>
            </div>

            {/* CSV Import */}
            <div className="mb-4">
              <button type="button" onClick={() => setCsvOpen(!csvOpen)} data-testid="pat-csv-toggle" className="brutalist-btn-outline !px-3 !py-1.5 text-xs flex items-center gap-1.5 mb-2">
                <Upload className="w-3.5 h-3.5" /> {csvOpen ? "Close CSV Import" : "Import from CSV"}
              </button>
              {csvOpen && (
                <div className="border-2 p-4 mb-2" style={{ borderColor: '#8B5CF6', backgroundColor: 'var(--bg-card)' }} data-testid="pat-csv-panel">
                  <p className="text-xs mb-2" style={{ color: 'var(--text-secondary)' }}>
                    Paste CSV or upload a .csv file. Expected columns: <strong>Location, Appliance ID, Description, Visual, Polarity, Earth (Ohms), Insulation (MOhms)</strong>
                  </p>
                  <p className="text-[10px] mb-2" style={{ color: 'var(--text-muted)' }}>
                    Example: <code className="px-1 py-0.5 border" style={{ borderColor: 'var(--border-light)' }}>Location,Appliance ID,Description,Visual,Polarity,Earth Ohms,Insulation MOhms</code>
                  </p>
                  <textarea
                    data-testid="pat-csv-textarea"
                    className="brutalist-input !text-xs font-mono min-h-[80px] resize-y mb-2"
                    placeholder={"Location,Appliance ID,Description,Visual,Polarity,Earth Ohms,Insulation MOhms\nOffice 1,1001,Monitor,P,P,0.05,>199\nOffice 1,1002,Kettle,P,P,0.03,>199"}
                    value={csvText}
                    onChange={(e) => setCsvText(e.target.value)}
                  />
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => parseCSV(csvText)} disabled={!csvText.trim()} data-testid="pat-csv-import-btn"
                      className="brutalist-btn !px-3 !py-1.5 text-xs flex items-center gap-1.5" style={{ backgroundColor: '#8B5CF6' }}>
                      <Upload className="w-3.5 h-3.5" /> Import
                    </button>
                    <label className="brutalist-btn-outline !px-3 !py-1.5 text-xs flex items-center gap-1.5 cursor-pointer">
                      <Upload className="w-3.5 h-3.5" /> Upload .csv
                      <input ref={fileRef} type="file" accept=".csv,.txt" className="hidden" onChange={handleCSVFile} />
                    </label>
                    <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                      {csvText.trim() ? `${csvText.trim().split("\n").length - 1} rows detected` : ""}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-2 mb-4">
              {appliances.map((app, idx) => (
                <div key={idx} className="border-2 p-3" style={{ borderColor: app.overall === "FAIL" ? "#EF4444" : 'var(--border-light)', backgroundColor: app.overall === "FAIL" ? "rgba(239,68,68,0.04)" : 'var(--bg-card)' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-bold w-6" style={{ color: 'var(--text-muted)' }}>{idx + 1}</span>
                    <span className={`text-xs font-bold px-2 py-0.5 border ${app.overall === "PASS" ? "text-green-700 border-green-300 bg-green-50" : "text-red-700 border-red-300 bg-red-50"}`}>{app.overall}</span>
                    <div className="flex-1" />
                    <button type="button" onClick={() => duplicateAppliance(idx)} className="text-xs text-[#8B5CF6] hover:underline">Duplicate</button>
                    {appliances.length > 1 && <button type="button" onClick={() => removeAppliance(idx)} className="text-red-500 hover:text-red-700"><Trash2 className="w-3.5 h-3.5" /></button>}
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 lg:grid-cols-8 gap-2">
                    <div className="col-span-2 sm:col-span-1">
                      <label className="text-[10px] uppercase font-bold" style={{ color: 'var(--text-muted)' }}>Location</label>
                      <input className="brutalist-input !text-xs !py-1" value={app.location} onChange={(e) => updateAppliance(idx, "location", e.target.value)} placeholder="e.g. Office 1" />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase font-bold" style={{ color: 'var(--text-muted)' }}>Appliance ID</label>
                      <input className="brutalist-input !text-xs !py-1" value={app.appliance_id} onChange={(e) => updateAppliance(idx, "appliance_id", e.target.value)} />
                    </div>
                    <div className="col-span-2 sm:col-span-1">
                      <label className="text-[10px] uppercase font-bold" style={{ color: 'var(--text-muted)' }}>Description</label>
                      <input className="brutalist-input !text-xs !py-1" value={app.description} onChange={(e) => updateAppliance(idx, "description", e.target.value)} placeholder="e.g. Monitor" />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase font-bold" style={{ color: 'var(--text-muted)' }}>Visual</label>
                      <select className="brutalist-input !text-xs !py-1" value={app.visual} onChange={(e) => updateAppliance(idx, "visual", e.target.value)}>
                        {PFS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] uppercase font-bold" style={{ color: 'var(--text-muted)' }}>Polarity</label>
                      <select className="brutalist-input !text-xs !py-1" value={app.polarity} onChange={(e) => updateAppliance(idx, "polarity", e.target.value)}>
                        {PFS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] uppercase font-bold" style={{ color: 'var(--text-muted)' }}>Earth (Ω)</label>
                      <input className="brutalist-input !text-xs !py-1" value={app.earth_ohms} onChange={(e) => updateAppliance(idx, "earth_ohms", e.target.value)} placeholder="0.05" />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase font-bold" style={{ color: 'var(--text-muted)' }}>Earth</label>
                      <select className="brutalist-input !text-xs !py-1" value={app.earth_result} onChange={(e) => updateAppliance(idx, "earth_result", e.target.value)}>
                        {PFS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] uppercase font-bold" style={{ color: 'var(--text-muted)' }}>Insulation (MΩ)</label>
                      <input className="brutalist-input !text-xs !py-1" value={app.insulation_mohms} onChange={(e) => updateAppliance(idx, "insulation_mohms", e.target.value)} placeholder=">199" />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase font-bold" style={{ color: 'var(--text-muted)' }}>Insulation</label>
                      <select className="brutalist-input !text-xs !py-1" value={app.insulation_result} onChange={(e) => updateAppliance(idx, "insulation_result", e.target.value)}>
                        {PFS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>
                  </div>
                  {app.overall === "FAIL" && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2 pt-2 border-t" style={{ borderColor: 'var(--border-light)' }}>
                      <div>
                        <label className="text-[10px] uppercase font-bold text-red-600">Fail Reason</label>
                        <input className="brutalist-input !text-xs !py-1" value={app.fail_reason} onChange={(e) => updateAppliance(idx, "fail_reason", e.target.value)} placeholder="e.g. Cable is damaged" />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase font-bold text-red-600">Notes</label>
                        <input className="brutalist-input !text-xs !py-1" value={app.notes} onChange={(e) => updateAppliance(idx, "notes", e.target.value)} placeholder="e.g. REMOVE FROM USE" />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <button type="button" onClick={addAppliance} className="brutalist-btn-outline !px-4 !py-2 flex items-center gap-2 text-sm mb-4" data-testid="pat-add-appliance">
              <Plus className="w-4 h-4" /> Add Appliance
            </button>

            <div className="flex justify-between items-center">
              <button data-testid="pat-step-2-prev" onClick={() => setStep(1)} className="brutalist-btn-outline flex items-center gap-2">
                <ArrowLeft className="w-4 h-4" /> Previous
              </button>
              <button data-testid="pat-step-2-next" onClick={() => setStep(3)} className="brutalist-btn flex items-center gap-2">
                Review <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* STEP 3: Review & Generate */}
        {step === 3 && (
          <div className="form-container max-w-3xl mx-auto" data-testid="pat-step-3">
            <h2 className="text-xl font-bold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Review & Generate</h2>
            <p className="text-xs mb-6" style={{ color: 'var(--text-secondary)' }}>Review all details, then generate your PAT testing report.</p>

            {/* Summary */}
            <div className="border-b-2 pb-4 mb-4" style={{ borderColor: 'var(--border-light)' }}>
              <h3 className="text-sm font-bold text-[#8B5CF6] uppercase tracking-[0.15em] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Report Summary</h3>
              <div className="grid grid-cols-3 gap-3 mb-3">
                <div className="text-center p-3 border-2" style={{ borderColor: 'var(--border-light)' }}>
                  <p className="text-2xl font-bold" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{appliances.length}</p>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Total Appliances</p>
                </div>
                <div className="text-center p-3 border-2 border-green-200 bg-green-50">
                  <p className="text-2xl font-bold text-green-700" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{passCount}</p>
                  <p className="text-xs text-green-600">Passed</p>
                </div>
                <div className="text-center p-3 border-2 border-red-200 bg-red-50">
                  <p className="text-2xl font-bold text-red-700" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{failCount}</p>
                  <p className="text-xs text-red-600">Failed</p>
                </div>
              </div>
            </div>

            {/* Client & Report details */}
            <div className="border-b-2 pb-4 mb-4" style={{ borderColor: 'var(--border-light)' }}>
              <h3 className="text-sm font-bold text-[#8B5CF6] uppercase tracking-[0.15em] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Client & Report Details</h3>
              {[["Company", report.client_company], ["Address", report.client_address], ["Contact", report.client_contact],
                ["Order No", report.order_no], ["Cert No", report.cert_number], ["Test Period", report.test_period ? format(report.test_period, "dd/MM/yyyy") : ""],
                ["Engineer", report.engineer_name], ["Contractor", report.contractor_name],
              ].map(([l, v]) => v ? (
                <div key={l} className="flex justify-between py-1 text-sm">
                  <span style={{ color: 'var(--text-secondary)' }}>{l}</span>
                  <span className="font-semibold text-right max-w-[60%]">{v}</span>
                </div>
              ) : null)}
            </div>

            {/* Failed items highlight */}
            {failCount > 0 && (
              <div className="border-b-2 pb-4 mb-4" style={{ borderColor: 'var(--border-light)' }}>
                <h3 className="text-sm font-bold text-red-600 uppercase tracking-[0.15em] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Failed Appliances</h3>
                {appliances.filter((a) => a.overall === "FAIL").map((a, i) => (
                  <div key={i} className="py-2 border-b last:border-0" style={{ borderColor: 'var(--border-light)' }}>
                    <div className="flex justify-between text-sm">
                      <span className="font-semibold">{a.appliance_id || "—"} — {a.description || "Unknown"}</span>
                      <span className="text-red-600 font-bold">FAIL</span>
                    </div>
                    {a.fail_reason && <p className="text-xs text-red-500 mt-0.5">Reason: {a.fail_reason}</p>}
                    {a.notes && <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>Notes: {a.notes}</p>}
                  </div>
                ))}
              </div>
            )}

            {/* Signature & Save Template */}
            <div className="border-t-2 pt-5 mt-4 space-y-4" style={{ borderColor: 'var(--border-light)' }}>
              <SignaturePad label="Engineer's Signature" value={signature} onChange={setSignature} />
              <SaveTemplateButton formType="pat_testing" getFormData={() => ({ report })} />
            </div>

            <div className="flex flex-col sm:flex-row justify-between gap-3 mt-4">
              <button data-testid="pat-step-3-prev" onClick={() => setStep(2)} className="brutalist-btn-outline flex items-center gap-2">
                <ArrowLeft className="w-4 h-4" /> Previous
              </button>
              <div className="flex gap-3">
                <button data-testid="pat-preview-btn" onClick={handlePreview} disabled={previewing} className="brutalist-btn-outline flex items-center gap-2">
                  {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />} Preview PDF
                </button>
                <button data-testid="pat-generate-btn" onClick={handleGenerate} disabled={submitting} className="brutalist-btn flex items-center gap-2" style={{ backgroundColor: "#8B5CF6" }}>
                  {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating...</> : <><FileDown className="w-4 h-4" /> Generate Report</>}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <BuyCreditsModal open={showModal} onClose={closeModal} creditType={modalCreditType} onBuy={buyCredits} />
      <PdfPreviewModal open={showPreview} onClose={() => { setShowPreview(false); if (previewUrl) URL.revokeObjectURL(previewUrl); }} pdfUrl={previewUrl} loading={previewing} />
    </div>
  );
}
